import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toastError } from '../../../../shared/toastHelper';

function readFiltersFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    return status ? { q: { status_eq: status } } : {};
  } catch (_) {
    return {};
  }
}

function writeFiltersToUrl(filters) {
  try {
    const params = new URLSearchParams(window.location.search);
    if (filters && filters.q && filters.q.status_eq) {
      params.set('status', filters.q.status_eq);
    } else {
      params.delete('status');
    }
    const qs = params.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', url);
  } catch (_) {
    // noop in non-browser environments
  }
}

// The donor is a fan OR an organization — pass exactly one of fanId / organizationId.
export default function usePledges({ teamId, fanId, organizationId }) {
  const [pledges, setPledges]    = useState([]);
  const [rollup, setRollup]      = useState(null);
  const [loading, setLoading]    = useState(true);
  const [filters, setFiltersRaw] = useState(readFiltersFromUrl);
  // Tracks the active GET request so rapid filter changes cancel stale responses
  // and don't clobber newer data via setState-after-resolve. Axios 0.19 predates
  // AbortController support, so we use the CancelToken API.
  const activeFetchRef = useRef(null);

  const setFilters = useCallback((next) => {
    setFiltersRaw(next);
    writeFiltersToUrl(next);
  }, []);

  const statusFilter = filters.q?.status_eq || '';

  const fetchPledges = useCallback(async () => {
    if (activeFetchRef.current) activeFetchRef.current.cancel('stale');
    const source = axios.CancelToken.source();
    activeFetchRef.current = source;

    setLoading(true);
    try {
      const params = fanId ? { fan_id: fanId } : { organization_id: organizationId };
      if (statusFilter) params['q[status_eq]'] = statusFilter;
      const { data } = await axios.get(
        `/teams/${teamId}/crm/pledges.json`,
        { params, cancelToken: source.token }
      );
      setPledges(data.pledges);
      setRollup(data.rollup);
    } catch (e) {
      if (axios.isCancel(e)) return; // filter changed mid-flight
      toastError('Could not load pledges. Refresh and try again.');
    } finally {
      if (activeFetchRef.current === source) {
        activeFetchRef.current = null;
        setLoading(false);
      }
    }
  }, [teamId, fanId, organizationId, statusFilter]);

  useEffect(() => { fetchPledges(); }, [fetchPledges]);

  // Caller-handled errors (PledgeFormModal swallows 422/409 and toasts everything
  // else). These mutations stay as plain awaits so the caller's try/catch wins;
  // unhandled paths fall through to the caller's catch.
  const createPledge = async (attrs) => {
    const { data } = await axios.post(`/teams/${teamId}/crm/pledges.json`, { pledge: attrs });
    await fetchPledges();
    return data;
  };

  const updatePledge = async (id, attrs) => {
    const { data } = await axios.patch(`/teams/${teamId}/crm/pledges/${id}.json`, { pledge: attrs });
    await fetchPledges();
    return data;
  };

  const cancelPledge = async (id) => {
    await axios.delete(`/teams/${teamId}/crm/pledges/${id}.json`);
    await fetchPledges();
  };

  const previewSchedule = async (attrs) => {
    const { data } = await axios.post(
      `/teams/${teamId}/crm/pledges/preview_schedule.json`,
      { pledge: attrs }
    );
    return data;
  };

  return {
    pledges, rollup, loading, filters, setFilters,
    refresh: fetchPledges,
    createPledge, updatePledge, cancelPledge, previewSchedule,
  };
}
